/**
 * i18n initialization — static JSON imports, no runtime fetch.
 *
 * Usage:
 *   import { initI18n } from './i18n';
 *   await initI18n('nl');          // first call: init + set language
 *   await initI18n('de');          // subsequent: just changes language
 *
 * Changing the language does not touch the document's reading direction — an app that owns its page
 * calls `applyDocumentDirection` for that. See the note on that function.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { RTL_LANGUAGES } from './useDirection';

// ── EN (source) ──────────────────────────────────────────────────────────────
import enCommon from './locales/en/common.json';
import enWidget from './locales/en/widget.json';
import enInbox from './locales/en/inbox.json';

// ── NL ───────────────────────────────────────────────────────────────────────
import nlCommon from './locales/nl/common.json';
import nlWidget from './locales/nl/widget.json';
import nlInbox from './locales/nl/inbox.json';

// ── DE ───────────────────────────────────────────────────────────────────────
import deCommon from './locales/de/common.json';
import deWidget from './locales/de/widget.json';
import deInbox from './locales/de/inbox.json';

// ── FR ───────────────────────────────────────────────────────────────────────
import frCommon from './locales/fr/common.json';
import frWidget from './locales/fr/widget.json';
import frInbox from './locales/fr/inbox.json';

// ── ES ───────────────────────────────────────────────────────────────────────
import esCommon from './locales/es/common.json';
import esWidget from './locales/es/widget.json';
import esInbox from './locales/es/inbox.json';

// ── AR ───────────────────────────────────────────────────────────────────────
import arCommon from './locales/ar/common.json';
import arWidget from './locales/ar/widget.json';
import arInbox from './locales/ar/inbox.json';

// ── Resource bundle ──────────────────────────────────────────────────────────

const resources = {
  en: { common: enCommon, widget: enWidget, inbox: enInbox },
  nl: { common: nlCommon, widget: nlWidget, inbox: nlInbox },
  de: { common: deCommon, widget: deWidget, inbox: deInbox },
  fr: { common: frCommon, widget: frWidget, inbox: frInbox },
  es: { common: esCommon, widget: esWidget, inbox: esInbox },
  ar: { common: arCommon, widget: arWidget, inbox: arInbox },
};

// ── Public API ───────────────────────────────────────────────────────────────

let initialized = false;

const STORAGE_KEY = 'cp-i18n-lang';

/**
 * Set the reading direction on the host document.
 *
 * Only for apps that own their page (the inbox, the analytics panel). The embedded chat widget must
 * not call this: it renders into a shadow root on someone else's website, so flipping
 * `documentElement.dir` would mirror the club's own page rather than the chat. The widget sets `dir`
 * on its own root instead (see Widget.tsx).
 */
export function applyDocumentDirection(language: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dir = RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr';
}

/**
 * Initialize i18next on first call; change language on subsequent calls.
 * User-chosen language is persisted to localStorage and used as fallback on init.
 *
 * @param language  BCP-47 language code (default: 'en')
 */
export async function initI18n(language?: string): Promise<void> {
  const lng = language || localStorage.getItem(STORAGE_KEY) || 'en';

  if (!initialized) {
    await i18n.use(initReactI18next).init({
      resources,
      lng,
      fallbackLng: 'en',
      defaultNS: 'common',
      ns: ['common', 'widget', 'inbox'],
      // POEditor exports untranslated plural forms as empty strings (e.g. nl
      // `routers.agentCount_other`), and i18next treats "" as a valid
      // translation by default — so a router with 2+ agents rendered a blank
      // count. Falling through to English beats showing nothing.
      returnEmptyString: false,
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    });
    initialized = true;
    localStorage.setItem(STORAGE_KEY, lng);
    return;
  }

  if (i18n.language !== lng) {
    await i18n.changeLanguage(lng);
    localStorage.setItem(STORAGE_KEY, lng);
  }
}

export { i18n };
